// Frontend client for the Cost Transparency Dashboard API.
// All USD figures are produced server-side; this client only shapes the HTTP.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function j(method, path, { body, headers } = {}) {
  const res = await fetch(`${BACKEND}/api${path}`, {
    method,
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.headers.get("content-type")?.includes("text/csv")) {
    return await res.text();
  }
  const text = await res.text();
  const payload = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const detail = (payload && payload.detail) || `HTTP ${res.status}`;
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return payload;
}

export function costOverview(days = 30, { versionId } = {}) {
  const qs = new URLSearchParams({ days: String(days) });
  if (versionId) qs.set("rate_card_version", versionId);
  return j("GET", `/cost/overview?${qs.toString()}`);
}
export function costTopConsumers(limit = 10, { versionId } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (versionId) qs.set("rate_card_version", versionId);
  return j("GET", `/cost/top-consumers?${qs.toString()}`);
}
export function costRateCard() {
  return j("GET", `/cost/rate-card`);
}
export function costRateCardHistory(limit = 50) {
  return j("GET", `/cost/rate-card/history?limit=${limit}`);
}
export function costRateCardVersion(versionId) {
  return j("GET", `/cost/rate-card/history/${encodeURIComponent(versionId)}`);
}
export function updateCostRateCard(patch, { actor, actorRole }) {
  return j("PUT", `/cost/rate-card`, {
    body: patch,
    headers: {
      "X-Actor": actor || "",
      "X-Actor-Role": actorRole || "",
    },
  });
}
export async function downloadCostCsv(days = 90) {
  const csv = await j("GET", `/cost/export.csv?days=${days}`);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  _triggerDownload(blob, `cost-transparency-${days}d.csv`);
}

async function _fetchBlob(path) {
  const res = await fetch(`${BACKEND}/api${path}`);
  if (!res.ok) {
    const t = await res.text();
    const err = new Error(t || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return await res.blob();
}
function _triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadCostXlsx(days = 90, { versionId } = {}) {
  const qs = new URLSearchParams({ days: String(days) });
  if (versionId) qs.set("rate_card_version", versionId);
  const blob = await _fetchBlob(`/cost/export.xlsx?${qs.toString()}`);
  _triggerDownload(blob, `cost-transparency-${days}d${versionId ? "-replay" : ""}.xlsx`);
}
export async function downloadRateCardXlsx() {
  const blob = await _fetchBlob(`/cost/rate-card.xlsx`);
  _triggerDownload(blob, `cost-transparency-rate-card.xlsx`);
}

// Formatting helpers — USD, no decimals ≥ $100, two decimals < $100.
export function fmtUsd(v, { compact = false } = {}) {
  if (v === null || v === undefined || Number.isNaN(v)) return "n/a";
  const n = Math.abs(v);
  if (compact && n >= 1000) {
    const units = [
      { v: 1e9, s: "B" }, { v: 1e6, s: "M" }, { v: 1e3, s: "K" },
    ];
    for (const u of units) {
      if (n >= u.v) return `$${(v / u.v).toFixed(n / u.v >= 100 ? 0 : 2)}${u.s}`;
    }
  }
  if (n >= 100) return `$${Math.round(v).toLocaleString("en-US")}`;
  return `$${v.toFixed(2)}`;
}
